import * as React from 'react'

import type { EmUnit, NumberSegment } from '../types'
import { getElementSizeInEm, targetWidths } from '../utils/getElementSize'

type MeasurementAction = {
  element: HTMLElement
  type: 'remove' | 'width'
  nextSibling: Node | null
  oldWidth?: string
  newWidth?: string
}

/**
 * Custom hook to handle section measurements and animations
 */
export const useMeasureSection = (
  measuredRef: React.RefObject<HTMLSpanElement>,
  isFirstRender: boolean,
  segments: NumberSegment[],
): EmUnit | undefined => {
  const [width, setWidth] = React.useState<EmUnit>()
  const segmentValues = segments.map((segment) => segment.value).join('')

  React.useEffect(() => {
    const element = measuredRef.current
    if (!element) return

    if (isFirstRender) {
      setWidth(getElementSizeInEm(element).width)
      return
    }

    const actions: MeasurementAction[] = []
    const children = Array.from(element.children)

    // Process each child element
    children.forEach((child) => {
      if (!(child instanceof HTMLElement)) return

      if (child.dataset.state === 'exiting') {
        actions.push({
          element: child,
          type: 'remove',
          nextSibling: child.nextSibling,
        })
        child.remove()
      } else {
        const newWidth = targetWidths.get(child)
        if (newWidth) {
          actions.push({
            element: child,
            type: 'width',
            nextSibling: null,
            oldWidth: child.style.width,
            newWidth,
          })
          child.style.width = newWidth
        }
      }
    })

    // Update width measurement
    setWidth(getElementSizeInEm(element).width)

    // Cleanup: revert all changes
    actions.toReversed().forEach((action) => {
      if (action.type === 'remove' && element) {
        element.insertBefore(action.element, action.nextSibling)
      } else if (action.type === 'width') {
        action.element.style.width = action.oldWidth || ''
      }
    })
  }, [measuredRef, isFirstRender, segmentValues])

  return width
}
